import { CloseOutlined } from '@ant-design/icons';
import { useTutorial } from './TutorialContext';
import { TutorialName } from './types';
import styles from './index.module.scss';

interface StartTutorialButtonProps {
	tutorial: TutorialName;
}

/** Graduation cap — antd has no equivalent, and it reads as "walkthrough". */
const GraduationCapIcon = () => (
	<svg
		width="13"
		height="13"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M22 9 12 4 2 9l10 5 10-5Z" />
		<path d="M6 11.5V17c0 1.1 2.7 2.5 6 2.5s6-1.4 6-2.5v-5.5" />
	</svg>
);

/**
 * Starts (or exits) a guided tutorial. Safe to drop into any page header — the
 * tour itself is rendered once by the global layout, not here.
 */
const StartTutorialButton = ({ tutorial }: StartTutorialButtonProps) => {
	const { activeTutorial, startTutorial, exitTutorial } = useTutorial();
	const isRunning = activeTutorial === tutorial;

	const label = isRunning ? 'Exit Tutorial' : 'Start Tutorial';

	return (
		<button
			type="button"
			className={`${styles['start-tutorial-button']} ${
				isRunning ? styles['start-tutorial-button--active'] : ''
			}`}
			onClick={() => (isRunning ? exitTutorial() : startTutorial(tutorial))}
			// The label collapses to an icon on narrow screens, where this header
			// otherwise runs into the tab bar — so the button needs its own name.
			title={label}
			aria-label={label}
		>
			<span className={styles['start-tutorial-button__icon']}>
				{isRunning ? (
					<CloseOutlined style={{ fontSize: 11 }} />
				) : (
					<GraduationCapIcon />
				)}
			</span>
			<span className={styles['start-tutorial-button__label']}>{label}</span>
		</button>
	);
};

export default StartTutorialButton;
